import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.js';
/**
 * Wire schemas for the tariff-change-request surface — player-ux-wave
 * (TASK-387). Backed by `customers.tariff_change_requests` (migration 0089).
 *
 * A client (player owner) asks to change their tariff, or simply "please
 * contact me" (no specific tier). The admin resolves the request out-of-band —
 * the actual tariff change stays a manual admin action.
 *
 *   Player side:
 *     POST /player/tariff-request  — create (idempotent per open request).
 *     GET  /player/tariff-request  — current open request or null.
 *   Admin side (@AdminOrAbove):
 *     GET  /admin/tariff-requests?status=…  — keyset-paginated list.
 *     POST /admin/tariff-requests/:id/resolve — mark resolved.
 *
 * All request/response objects `.strict()` (drift guard).
 */
/** Tariff tier a client may request. Mirrors `internal.tariffs.code`. */
export const tariffRequestTierSchema = z.enum(['lite', 'standard', 'pro']);
/** Request lifecycle — mirrors the DB CHECK on `status`. */
export const tariffRequestStatusSchema = z.enum(['open', 'resolved']);
const COMMENT_MAX = 2_000;
const CURSOR_MAX = 200;
/**
 * `POST /player/tariff-request` request body. Both fields optional: a client
 * may request a specific `requested_tier`, leave a free-form `comment`, or
 * neither (a bare "please contact me").
 */
export const createTariffRequestSchema = z
    .object({
    requested_tier: tariffRequestTierSchema.nullish(),
    comment: z.string().trim().min(1).max(COMMENT_MAX).nullish(),
})
    .strict();
/**
 * A tariff-change request as surfaced to the client on `GET /player/tariff-
 * request` (the account's own current open request, or null).
 */
export const tariffRequestSchema = z
    .object({
    id: uuidSchema,
    account_id: uuidSchema,
    requested_tier: tariffRequestTierSchema.nullable(),
    comment: z.string().nullable(),
    status: tariffRequestStatusSchema,
    created_at: isoDateTimeSchema,
    resolved_at: isoDateTimeSchema.nullable(),
})
    .strict();
/**
 * `GET /player/tariff-request` response — the current OPEN request or null so
 * the FE can render "заявка отправлена" vs the request form.
 */
export const currentTariffRequestResponseSchema = z
    .object({
    request: tariffRequestSchema.nullable(),
})
    .strict();
/**
 * Admin list item — flat row with denormalised account fields (company name +
 * current tariff code) pulled via JOIN so the DataTable needs no per-row call.
 */
export const adminTariffRequestListItemSchema = z
    .object({
    id: uuidSchema,
    account_id: uuidSchema,
    company_name: z.string().nullable(),
    current_tariff: tariffRequestTierSchema.nullable(),
    requested_tier: tariffRequestTierSchema.nullable(),
    comment: z.string().nullable(),
    status: tariffRequestStatusSchema,
    created_at: isoDateTimeSchema,
    resolved_at: isoDateTimeSchema.nullable(),
    resolved_by: uuidSchema.nullable(),
    resolved_by_name: z.string().nullable(),
})
    .strict();
/**
 * `GET /admin/tariff-requests` query params. Keyset cursor encodes
 * `(created_at, id) DESC` (see service). `status` defaults to `open` — the
 * admin's primary view is the pending queue. `account_id` optionally narrows
 * the list to a single account (e.g. the account card's tariff-request tab).
 */
export const adminTariffRequestFilterSchema = z
    .object({
    status: tariffRequestStatusSchema.default('open'),
    account_id: uuidSchema.optional(),
    cursor: z.string().min(1).max(CURSOR_MAX).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(20),
})
    .strict();
/** `GET /admin/tariff-requests` response — keyset-paginated list. */
export const adminTariffRequestListResponseSchema = z
    .object({
    items: z.array(adminTariffRequestListItemSchema),
    next_cursor: z.string().nullable(),
})
    .strict();
/**
 * `POST /admin/tariff-requests/:id/resolve` response — the resolved request.
 * Idempotent: resolving an already-resolved request returns it unchanged.
 */
export const adminTariffRequestResolveResponseSchema = z
    .object({
    request: tariffRequestSchema,
})
    .strict();
//# sourceMappingURL=tariff-request.js.map